import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useManualBiometricUpdate } from 'helpers/hooks/useManualBiometricUpdate';
import { Button, ConfirmModal, DateInput } from '@atoms';
import { useFetchListCountries } from '@hooks';
import { BoxContainer } from '@layouts';
import { SelectFetch } from '@molecules';
import styles from './styles';

/**
 * `OnboardingValidateTab` is a component used to manually update biometric data
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.client - Client data
 * @param {string} props.client.id - Client ID
 * @param {string} props.client.username - Client username
 * @param {string} props.client.walletId - Client wallet ID
 * @param {string} [props.client.birthDate] - Pre-existing birth date, if available
 */
const OnboardingValidateTab = ({ client }) => {
  const [birthDate, setBirthDate] = useState(client.birthDate || '');
  const [nationalityId, setNationalityId] = useState('032');
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState({
    title: '',
    description: '',
    isError: false,
    showCancelButton: true,
  });
  const [isConfirmStep, setIsConfirmStep] = useState(false);
  const { trigger } = useManualBiometricUpdate();
  const { data: dataCountries } = useFetchListCountries();
  const selectedCountry =
    dataCountries?.find(({ isoNumericCode }) => isoNumericCode === nationalityId)?.name || '';
  const { t } = useTranslation();
  const today = new Date();
  const maxBirthDate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());

  /**
   * Open the confirmation modal and sets its content based on the current input.
   */
  const handleOpenModal = () => {
    setModalContent({
      title: t('EMPLOYEES:ONBOARDING:MODAL_TITLE'),
      description: (
        <>
          <strong>{t('EMPLOYEES:ONBOARDING:BIRTH_DATE')}:</strong>
          {new Date(birthDate).toLocaleDateString()}
          <br />
          <strong>{t('EMPLOYEES:ONBOARDING:NACIONALITY')}:</strong> {selectedCountry}
        </>
      ),
      isError: false,
      showCancelButton: true,
    });
    setIsConfirmStep(true);
    setShowModal(true);
  };

  /**
   * Submits the biometric update using the hook and shows the result
   */
  const handleConfirmSubmit = async () => {
    try {
      await trigger({
        username: client.username,
        walletUserId: client.walletId,
        userId: client.id,
        birthDate,
        nationalityId,
      });

      setModalContent({
        title: t('EMPLOYEES:ONBOARDING:MODAL:SUCCESS_TITLE'),
        description: t('EMPLOYEES:ONBOARDING:MODAL:SUCCESS_MESSAGE'),
        isError: false,
        showCancelButton: false,
      });
    } catch (err) {
      console.error(err);
      setModalContent({
        title: t('EMPLOYEES:ONBOARDING:MODAL:ERROR_TITLE'),
        description: t('EMPLOYEES:ONBOARDING:MODAL:ERROR_MESSAGE'),
        isError: true,
        showCancelButton: false,
      });
    } finally {
      setIsConfirmStep(false);
      setShowModal(true);
    }
  };

  return (
    <div style={styles.container}>
      <ConfirmModal
        modalVisible={showModal}
        setModalVisible={setShowModal}
        title={modalContent.title}
        description={modalContent.description}
        showCancelButton={modalContent.showCancelButton}
        handleSubmit={isConfirmStep ? handleConfirmSubmit : () => setShowModal(false)}
        error={modalContent.isError}
      />
      <BoxContainer>
        <h3>{t('EMPLOYEES:ONBOARDING:TITLE')}</h3>
        <section style={styles.formRow}>
          <DateInput
            label={t('EMPLOYEES:ONBOARDING:BIRTH_DATE')}
            value={birthDate}
            onChange={(e) => setBirthDate(e.target.value)}
            required
            maxDate={maxBirthDate}
          />
          <SelectFetch
            placeholder={t('EMPLOYEES:ONBOARDING:NACIONALITY')}
            value={nationalityId}
            defaultValue={[]}
            onChange={(e) => setNationalityId(e.target.value)}
            getOptions={useFetchListCountries}
            parseOption={(item) => ({ value: item.isoNumericCode, text: item.name })}
          />
        </section>
        <section style={styles.actions}>
          <Button
            onClick={handleOpenModal}
            text={t('EMPLOYEES:ONBOARDING:BUTTON_TEXT')}
            disabled={!birthDate || !nationalityId}
            variant='outlined'
            color='primary'
          />
        </section>
      </BoxContainer>
    </div>
  );
};

export default OnboardingValidateTab;

OnboardingValidateTab.propTypes = {
  client: PropTypes.shape({
    id: PropTypes.string.isRequired,
    username: PropTypes.string.isRequired,
    walletId: PropTypes.string.isRequired,
    birthDate: PropTypes.string,
  }).isRequired,
};
